import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { USE_MOCK_DATA } from '../services/mockData';
import './DishCard.css';

function DishCard({ dish, user, onVote, onFavoriteChange }) {
  const [voteCount, setVoteCount] = useState(dish.voteCount || 0);
  const [userHasVoted, setUserHasVoted] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  useEffect(() => {
    const checkUserVote = async () => {
      if (!user) {
        setUserHasVoted(false);
        return;
      }

      try {
        if (USE_MOCK_DATA) {
          // In mock mode, check localStorage
          const mockVotes = JSON.parse(localStorage.getItem('mockVotes') || '[]');
          const hasVoted = mockVotes.some(v => v.dishId === dish.id && v.userId === user.uid);
          setUserHasVoted(hasVoted);
          return;
        }

        const votesQuery = query(
          collection(db, 'votes'),
          where('dishId', '==', dish.id),
          where('userId', '==', user.uid)
        );
        const votesSnapshot = await getDocs(votesQuery);
        setUserHasVoted(!votesSnapshot.empty);
      } catch (error) {
        console.error('Error checking vote:', error);
        // Fallback to localStorage check
        try {
          const mockVotes = JSON.parse(localStorage.getItem('mockVotes') || '[]');
          const hasVoted = mockVotes.some(v => v.dishId === dish.id && v.userId === user.uid);
          setUserHasVoted(hasVoted);
        } catch {
          setUserHasVoted(false);
        }
      }
    };

    const checkUserFavorite = async () => {
      if (!user) {
        setIsFavorite(false);
        return;
      }

      try {
        const favoriteQuery = query(
          collection(db, 'favorites'),
          where('dishId', '==', dish.id),
          where('userId', '==', user.uid)
        );
        const favoriteSnapshot = await getDocs(favoriteQuery);
        setIsFavorite(!favoriteSnapshot.empty);
      } catch (error) {
        console.error('Error checking favorite:', error);
      }
    };

    checkUserVote();
    checkUserFavorite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dish.id, user]);

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      alert('Please login to save favorites');
      return;
    }

    setIsTogglingFavorite(true);
    try {
      const favoriteQuery = query(
        collection(db, 'favorites'),
        where('dishId', '==', dish.id),
        where('userId', '==', user.uid)
      );
      const favoriteSnapshot = await getDocs(favoriteQuery);

      if (!favoriteSnapshot.empty) {
        // Remove favorite
        await deleteDoc(favoriteSnapshot.docs[0].ref);
        setIsFavorite(false);
      } else {
        // Add favorite
        await addDoc(collection(db, 'favorites'), {
          userId: user.uid,
          dishId: dish.id,
          createdAt: new Date()
        });
        setIsFavorite(true);
      }

      if (onFavoriteChange) {
        onFavoriteChange();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Error saving favorite. Please try again.');
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const handleVote = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      alert('Please login to vote');
      return;
    }

    if (userHasVoted) {
      return;
    }

    setIsVoting(true);
    try {
      if (USE_MOCK_DATA) {
        // In mock mode, use localStorage
        const mockVotes = JSON.parse(localStorage.getItem('mockVotes') || '[]');
        mockVotes.push({
          dishId: dish.id,
          userId: user.uid,
          createdAt: new Date().toISOString()
        });
        localStorage.setItem('mockVotes', JSON.stringify(mockVotes));

        const newVoteCount = voteCount + 1;
        setVoteCount(newVoteCount);
        setUserHasVoted(true);

        if (onVote) {
          onVote();
        }
        setIsVoting(false);
        return;
      }

      // Add vote
      await addDoc(collection(db, 'votes'), {
        dishId: dish.id,
        userId: user.uid,
        createdAt: new Date()
      });

      // Update dish vote count
      const newVoteCount = voteCount + 1;
      await updateDoc(doc(db, 'dishes', dish.id), {
        voteCount: newVoteCount
      });

      setVoteCount(newVoteCount);
      setUserHasVoted(true);

      if (onVote) {
        onVote();
      }
    } catch (error) {
      console.error('Error voting:', error);
      if (error.code === 'permission-denied') {
        alert('Firebase permissions error. Add ?mock=true to URL for demo mode.');
      } else {
        alert('Error voting. Please try again.');
      }
    } finally {
      setIsVoting(false);
    }
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className="star full">★</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half" className="star half">★</span>);
    }
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className="star empty">★</span>);
    }

    return stars;
  };

  return (
    <div className="dish-card-wrapper">
      <Link to={`/dish/${dish.id}`} className="dish-card">
        <div className="dish-image-container">
          {dish.imageUrl ? (
            <img src={dish.imageUrl} alt={dish.name} className="dish-image" />
          ) : (
            <div className="dish-image-placeholder">
              <span className="placeholder-icon">🍽️</span>
            </div>
          )}
          <div className="dish-rating-badge">
            <div className="badge-stars">{renderStars(dish.averageRating || 0)}</div>
            <span className="badge-rating">
              {dish.averageRating ? dish.averageRating.toFixed(1) : '0.0'}
            </span>
          </div>
          {user && (
            <button
              onClick={handleToggleFavorite}
              disabled={isTogglingFavorite}
              className={`favorite-button ${isFavorite ? 'favorited' : ''}`}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <span className="favorite-icon">{isFavorite ? '❤️' : '🤍'}</span>
            </button>
          )}
        </div>

        <div className="dish-info">
          <h3 className="dish-name">{dish.name}</h3>
          <div className="dish-meta">
            {dish.dishType && (
              <span className="dish-type-badge">{dish.dishType}</span>
            )}
            {dish.cuisine && (
              <span className="cuisine-badge">{dish.cuisine}</span>
            )}
          </div>
          {dish.restaurantName && (
            <p className="dish-restaurant">📍 {dish.restaurantName}</p>
          )}
          {dish.description && (
            <p className="dish-description-preview">{dish.description.substring(0, 80)}...</p>
          )}
          <div className="dish-rating-info">
            <span className="rating-count">
              {dish.totalRatings || 0} {dish.totalRatings === 1 ? 'rating' : 'ratings'}
            </span>
          </div>
        </div>
      </Link>

      <div className="dish-vote-section">
        <button
          onClick={handleVote}
          disabled={!user || userHasVoted || isVoting}
          className={`vote-button ${userHasVoted ? 'voted' : ''}`}
          title={userHasVoted ? 'You already voted' : user ? 'Vote for this dish' : 'Login to vote'}
        >
          <span className="vote-icon">👍</span>
          <span className="vote-count">{voteCount}</span>
        </button>
      </div>
    </div>
  );
}

export default DishCard;
